// Folds a craft, its engine and the installed modifications into the numbers the tick functions read.
// Multiplicative ModEffect fields multiply, additive fields add; the fold is order-independent.
import type { CraftSpec, EngineSpec, ModSpec, HullType, Controls, Mixture, AirframeSpec } from '../types';
import { ftToM, speedOfSound } from './atmosphere';

export interface AirEff {
  emptyMassKg: number;
  payloadKg: number;
  fuelCapacityKg: number;
  wingAreaM2: number;
  aspectRatio: number;
  oswald: number;
  cd0: number;
  inducedMul: number;
  vDesignMs: number;
  vStallMs: number;
  ceilingM: number;
  engineCount: number;
  /** Per engine, kW (shaft engines). */
  ratedPowerKw: number;
  /** Per engine, N (jets). */
  ratedThrustN: number;
  afterburnerThrustN: number;
  bsfcKgPerKwh: number;
  tsfcKgPerNh: number;
  tsfcAbKgPerNh: number;
  criticalAltM: number;
  propeller: 'fixed' | 'constant-speed';
  propEffMul: number;
  fuelInjected: boolean;
  lopEnabled: boolean;
  /** Fuel carried in jettisonable external tanks (part of fuelCapacityKg). */
  storesFuelKg: number;
  /** Structure mass of the jettisonable tanks (part of emptyMassKg until dropped). */
  storesMassKg: number;
  /** Drag of the jettisonable tanks (part of cd0 until dropped). */
  storesCd0: number;
  /** Drag-divergence Mach: wave drag grows as 20·(M − machCrit)⁴ above it. */
  machCrit: number;
  /** Autothrottle Mach cap. */
  machMax: number;
}

export interface SeaEff {
  hullType: HullType;
  lwlM: number;
  designSpeedKn: number;
  designPowerKw: number;
  hotelKw: number;
  fuelCapacityKg: number;
  minSpeedKn: number;
  maxSpeedKn: number;
  engineCount: number;
  /** Per engine, kW. */
  ratedPowerKw: number;
  bsfcKgPerKwh: number;
  /** Fouling at the start of a run (fraction of added power). */
  foulingInitial: number;
  foulingGrowthMul: number;
  addedMassKg: number;
}

export interface ResolvedCraft {
  craft: CraftSpec;
  engine: EngineSpec;
  mods: ModSpec[];
  air?: AirEff;
  sea?: SeaEff;
}

/**
 * estimate: stock vessels start a run with the "deteriorated coating / light slime" penalty of +9 % shaft power
 * from Schultz et al. 2011 (DDG-51 at 15 kn); the hull-cleaning mod resets it to 0.
 */
export const STOCK_FOULING = 0.09;

/**
 * Compressibility limits. Airframe polars carry no Mach fields, so a civil jet's autothrottle Mach is derived from
 * its `vDesignMs` read as the long-range-cruise TAS at 35,000 ft (e.g. the 737-800's published 452 kn LRC → M 0.79);
 * the drag rise starts 0.08 below it (estimate). Afterburning fighters: rise from M 0.9, cap M 2.0 (estimate).
 */
function machLimits(engine: EngineSpec, airframe: AirframeSpec): { crit: number; max: number } {
  if (engine.kind === 'turbofan') {
    if (engine.afterburnerThrustN) return { crit: 0.9, max: 2.0 };
    const max = Math.max(0.7, Math.min(0.86, airframe.vDesignMs / speedOfSound(ftToM(35000))));
    return { crit: max - 0.08, max };
  }
  return { crit: 0.6, max: 0.7 };
}

function mul(a: number | undefined, b: number): number {
  return a === undefined ? b : a * b;
}

export function resolveBuild(craft: CraftSpec, engine: EngineSpec, mods: ModSpec[]): ResolvedCraft {
  let bsfcMul = 1, tsfcMul = 1, powerMul = 1, thrustMul = 1, propEffMul = 1, inducedMul = 1, propulsiveMul = 1;
  let hotelMul = 1, foulingGrowthMul = 1, foulingReset = false, enablesLop = false;
  let cd0Add = 0, massAdd = 0, fuelAdd = 0, storesFuel = 0, storesMass = 0, storesCd0 = 0;
  let criticalAltM: number | undefined = engine.criticalAltM;
  let propeller: 'fixed' | 'constant-speed' = engine.propeller ?? 'constant-speed';
  for (const m of mods) {
    const e = m.effect;
    bsfcMul = mul(e.bsfcMul, bsfcMul);
    tsfcMul = mul(e.tsfcMul, tsfcMul);
    powerMul = mul(e.powerMul, powerMul);
    thrustMul = mul(e.thrustMul, thrustMul);
    propEffMul = mul(e.propEffMul, propEffMul);
    inducedMul = mul(e.inducedMul, inducedMul);
    propulsiveMul = mul(e.propulsivePowerMul, propulsiveMul);
    hotelMul = mul(e.hotelMul, hotelMul);
    foulingGrowthMul = mul(e.foulingGrowthMul, foulingGrowthMul);
    if (e.foulingReset) foulingReset = true;
    if (e.enablesLop) enablesLop = true;
    if (e.propeller) propeller = e.propeller;
    if (e.criticalAltM !== undefined) criticalAltM = Math.max(criticalAltM ?? 0, e.criticalAltM);
    cd0Add += e.cd0Add ?? 0;
    massAdd += e.emptyMassAddKg ?? 0;
    fuelAdd += e.fuelCapacityAddKg ?? 0;
    if (e.jettisonWhenEmpty) {
      storesFuel += e.fuelCapacityAddKg ?? 0;
      storesMass += e.emptyMassAddKg ?? 0;
      storesCd0 += e.cd0Add ?? 0;
    }
  }
  const rc: ResolvedCraft = { craft, engine, mods };
  if (craft.airframe) {
    const a = craft.airframe;
    const mach = machLimits(engine, a);
    rc.air = {
      emptyMassKg: a.emptyMassKg + massAdd,
      payloadKg: a.payloadKg,
      fuelCapacityKg: a.fuelCapacityKg + fuelAdd,
      wingAreaM2: a.wingAreaM2,
      aspectRatio: a.aspectRatio,
      oswald: a.oswald,
      cd0: a.cd0 + cd0Add,
      inducedMul,
      vDesignMs: a.vDesignMs,
      vStallMs: a.vStallMs,
      ceilingM: a.ceilingM,
      engineCount: a.engineCount,
      ratedPowerKw: (engine.ratedPowerKw ?? 0) * powerMul,
      ratedThrustN: (engine.ratedThrustN ?? 0) * thrustMul,
      afterburnerThrustN: (engine.afterburnerThrustN ?? 0) * thrustMul,
      bsfcKgPerKwh: (engine.bsfcKgPerKwh ?? 0) * bsfcMul,
      tsfcKgPerNh: (engine.tsfcKgPerNh ?? 0) * tsfcMul,
      tsfcAbKgPerNh: (engine.tsfcAfterburnerKgPerNh ?? 0) * tsfcMul,
      criticalAltM: criticalAltM ?? 0,
      propeller,
      propEffMul,
      fuelInjected: engine.fuelInjected ?? false,
      lopEnabled: enablesLop,
      storesFuelKg: storesFuel,
      storesMassKg: storesMass,
      storesCd0: storesCd0,
      machCrit: mach.crit,
      machMax: mach.max,
    };
  }
  if (craft.hull) {
    const h = craft.hull;
    rc.sea = {
      hullType: h.hullType,
      lwlM: h.lwlM,
      designSpeedKn: h.designSpeedKn,
      designPowerKw: h.designPowerKw * propulsiveMul,
      hotelKw: h.hotelKw * hotelMul,
      fuelCapacityKg: h.fuelCapacityKg + fuelAdd,
      minSpeedKn: h.minSpeedKn,
      maxSpeedKn: h.maxSpeedKn,
      engineCount: h.engineCount,
      ratedPowerKw: (engine.ratedPowerKw ?? 0) * powerMul,
      bsfcKgPerKwh: (engine.bsfcKgPerKwh ?? 0) * bsfcMul,
      foulingInitial: foulingReset ? 0 : STOCK_FOULING,
      foulingGrowthMul,
      addedMassKg: massAdd,
    };
  }
  return rc;
}

export function airEff(rc: ResolvedCraft): AirEff {
  if (!rc.air) throw new Error(`${rc.craft.id} is not an aircraft`);
  return rc.air;
}

export function seaEff(rc: ResolvedCraft): SeaEff {
  if (!rc.sea) throw new Error(`${rc.craft.id} is not a vessel`);
  return rc.sea;
}

export function isJet(rc: ResolvedCraft): boolean {
  return rc.engine.kind === 'turbofan';
}

/** Maximum mass at the start of a run (empty + stores + payload + full fuel). */
export function maxMassKg(eff: AirEff): number {
  return eff.emptyMassKg + eff.payloadKg + eff.fuelCapacityKg;
}

/** Mixtures the equipment allows at this power setting (spec §7: peak/lean only at ≤ 75 %). */
export function allowedMixtures(rc: ResolvedCraft, power: number): Mixture[] {
  const out: Mixture[] = ['rich'];
  if (rc.engine.kind !== 'piston' || power > 0.75 + 1e-9) return out;
  out.push('peak');
  if (rc.air && rc.air.fuelInjected && rc.air.lopEnabled) out.push('lop');
  return out;
}

/** Clamps a cruise plan to what the craft and its equipment can actually do. The tick functions take the result as given. */
export function sanitizeControls(rc: ResolvedCraft, c: Controls): Controls {
  const out: Controls = { ...c };
  if (rc.air) {
    const a = rc.air;
    out.altitudeFt = Math.max(0, Math.min(c.altitudeFt ?? 0, a.ceilingM / ftToM(1)));
    if (isJet(rc)) {
      out.thrust = Math.max(0.3, Math.min(1, c.thrust ?? 0.6));
      if (!a.afterburnerThrustN) out.afterburner = false;
    } else {
      out.power = Math.max(0.3, Math.min(1, c.power ?? 0.65));
      const allowed = allowedMixtures(rc, out.power);
      const want = c.mixture ?? 'rich';
      out.mixture = allowed.includes(want) ? want : allowed[allowed.length - 1];
    }
  }
  if (rc.sea) {
    const s = rc.sea;
    out.speedKn = Math.max(s.minSpeedKn, Math.min(s.maxSpeedKn, c.speedKn ?? s.designSpeedKn));
  }
  return out;
}
